import React from "react";
import { reportFrontendCrash } from "../../utils/crashReporter";

export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    const msg = String(error?.message || error || "").toLowerCase();
    const stack = String(error?.stack || "").toLowerCase();

    // AGGRESSIVE: Suppress ALL Ably lifecycle errors
    // These are NOT crashes - they're normal state transitions
    const ablyKeywords = [
      "detach request superseded",
      "attach request superseded",
      "subsequent attach request",
      "channel detached",
      "channel is detached",
      "channel has detached",
      "channel failed",
      "channel suspended",
      "connection to server unavailable",
      "realtimechannel.onmessage",  // Ably internal handler
      "websockettransport.onwsdata", // Ably WebSocket layer
      "errorinfo"                    // Ably error class
    ];

    const isAblyError = ablyKeywords.some(keyword => 
      msg.includes(keyword) || stack.includes(keyword)
    );

    if (isAblyError) {
      console.warn("[ABLY-ERROR-BOUNDARY] Ignoring Ably lifecycle error:", error?.message || error);
      return { hasError: false, error: null, errorInfo: null };
    }

    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const msg = String(error?.message || error || "").toLowerCase();
    const stack = String(error?.stack || "").toLowerCase();

    // AGGRESSIVE: Ignore ALL Ably lifecycle events completely
    const ablyKeywords = [
      "superseded",
      "channel detached",
      "channel is detached",
      "channel has detached",
      "connection to server unavailable",
      "realtimechannel",
      "websockettransport",
      "errorinfo"
    ];

    const isAblyError = ablyKeywords.some(keyword => 
      msg.includes(keyword) || stack.includes(keyword)
    );

    if (isAblyError) {
      // Completely ignore - don't even log
      return;
    }

    // Store errorInfo for display
    this.setState({ errorInfo });

    // Log the error
    console.error("[ERROR_BOUNDARY] React crash captured:", error, errorInfo);

    // Report to backend guardians (fire-and-forget, non-blocking)
    reportFrontendCrash({ error, info: errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/dashboard";
  };

  handleCopyDebug = async () => {
    try {
      const payload = {
        time: new Date().toISOString(),
        location: window.location.href,
        userAgent: navigator.userAgent,
        error: this.state.error ? String(this.state.error) : null,
        stack: this.state.error?.stack || null,
        componentStack: this.state.errorInfo?.componentStack || null,
      };
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      // eslint-disable-next-line no-alert
      alert("Debug info copied to clipboard.");
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Could not copy debug info.");
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen w-full flex items-center justify-center p-4 bg-slate-950">
          <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 shadow-lg p-6">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-rose-400 text-2xl">!</span>
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-semibold text-white">
                  Something went wrong
                </h1>
                <p className="text-sm text-slate-300 mt-1">
                  The app hit an unexpected error. Try refreshing. If it keeps happening,
                  copy the debug info and send it to support.
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={this.handleReload}
                className="px-4 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-500 transition-colors"
              >
                Refresh
              </button>

              <button
                onClick={this.handleGoHome}
                className="px-4 py-2.5 rounded-xl bg-slate-700 text-white text-sm font-medium hover:bg-slate-600 transition-colors"
              >
                Go to Dashboard
              </button>

              <button
                onClick={this.handleCopyDebug}
                className="px-4 py-2.5 rounded-xl bg-slate-700 text-white text-sm font-medium hover:bg-slate-600 transition-colors"
              >
                Copy debug info
              </button>
            </div>

            <details className="mt-5">
              <summary className="text-sm text-slate-400 cursor-pointer select-none hover:text-slate-300">
                Technical details
              </summary>
              <div className="mt-3 text-xs text-slate-300 whitespace-pre-wrap break-words bg-slate-800 border border-slate-700 rounded-xl p-4 max-h-64 overflow-auto">
                <div className="font-semibold text-slate-200 mb-1">Error</div>
                <div className="text-red-400">{this.state.error ? String(this.state.error) : "Unknown error"}</div>

                {this.state.error?.stack ? (
                  <>
                    <div className="font-semibold text-slate-200 mt-4 mb-1">Stack</div>
                    <div className="text-slate-400 text-[11px]">{this.state.error.stack}</div>
                  </>
                ) : null}

                {this.state.errorInfo?.componentStack ? (
                  <>
                    <div className="font-semibold text-slate-200 mt-4 mb-1">Component stack</div>
                    <div className="text-slate-400 text-[11px]">{this.state.errorInfo.componentStack}</div>
                  </>
                ) : null}
              </div>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
